import firebase, { functions, storage } from "../firebase";

export type MediaKind = "image" | "video";
export type MediaProvider = "firebase" | "r2";

export type StoredMedia = {
  url: string;
  storageProvider: MediaProvider;
  mediaAssetId?: string;
  /** Present only for the legacy Firebase fallback. */
  storagePath?: string;
};

type UploadSession = {
  assetId: string;
  state: string;
  uploadUrl?: string;
  requiredHeaders?: Record<string, string>;
};

type ReadyAsset = {
  id: string;
  state: string;
  deliveryUrl?: string;
};

const R2_MEDIA_ENABLED =
  process.env.REACT_APP_R2_MEDIA_ENABLED !== "false";
const FIREBASE_FALLBACK_ENABLED =
  process.env.REACT_APP_R2_MEDIA_FIREBASE_FALLBACK !== "false";

const CONTENT_TYPE_BY_EXTENSION: Record<string, string> = {
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  png: "image/png",
  webp: "image/webp",
  gif: "image/gif",
  avif: "image/avif",
  heic: "image/heic",
  heif: "image/heif",
  mp4: "video/mp4",
  mov: "video/quicktime",
  webm: "video/webm",
  m4v: "video/x-m4v",
};

function extension(value: string): string {
  return value
    .split(/[?#]/, 1)[0]
    .split(".")
    .pop()
    ?.toLowerCase() || "";
}

export function normalizeMediaContentType(
  value: string | undefined,
  fileName = ""
): string {
  const normalized = (value || "").split(";", 1)[0].trim().toLowerCase();
  if (normalized === "image/jpg") return "image/jpeg";
  if (normalized) return normalized;
  return CONTENT_TYPE_BY_EXTENSION[extension(fileName)] || "";
}

export function inferMediaKind(
  contentType: string,
  fileName = ""
): MediaKind | undefined {
  if (contentType.startsWith("image/")) return "image";
  if (contentType.startsWith("video/")) return "video";
  const inferred = CONTENT_TYPE_BY_EXTENSION[extension(fileName)] || "";
  if (inferred.startsWith("image/")) return "image";
  if (inferred.startsWith("video/")) return "video";
  return undefined;
}

export function mediaAssetIdFromUrl(value: string | null | undefined):
  | string
  | undefined {
  if (!value) return undefined;
  try {
    const url = new URL(value);
    if (
      url.hostname !== "media.tribed.io" ||
      url.searchParams.get("v") !== "1"
    ) {
      return undefined;
    }
    const match = /^\/o\/([A-Za-z0-9_-]+)$/.exec(url.pathname);
    if (!match) return undefined;
    const base64 = match[1]
      .replace(/-/g, "+")
      .replace(/_/g, "/")
      .padEnd(Math.ceil(match[1].length / 4) * 4, "=");
    const objectKey = atob(base64);
    if (!objectKey.startsWith("media/")) return undefined;
    const filename = objectKey.split("/").pop() || "";
    const assetId = filename.replace(/\.[A-Za-z0-9]{1,10}$/, "");
    return /^[A-Za-z0-9_-]{16,128}$/.test(assetId)
      ? assetId
      : undefined;
  } catch {
    return undefined;
  }
}

export function isFirebaseStorageUrl(value: string): boolean {
  return (
    value.startsWith("gs://") ||
    value.includes("firebasestorage.googleapis.com") ||
    value.includes("storage.googleapis.com")
  );
}

function putToSignedUrl(input: {
  url: string;
  body: Blob;
  headers: Record<string, string>;
  onProgress?: (fraction: number) => void;
}): Promise<void> {
  return new Promise((resolve, reject) => {
    const request = new XMLHttpRequest();
    request.open("PUT", input.url);
    Object.entries(input.headers).forEach(([name, value]) => {
      request.setRequestHeader(name, value);
    });
    request.upload.onprogress = (event) => {
      if (input.onProgress && event.lengthComputable && event.total > 0) {
        input.onProgress(event.loaded / event.total);
      }
    };
    request.onerror = () =>
      reject(new Error("Cloudflare R2 upload failed."));
    request.onload = () => {
      if (request.status >= 200 && request.status < 300) {
        resolve();
      } else {
        reject(
          new Error(`Cloudflare R2 upload failed with HTTP ${request.status}.`)
        );
      }
    };
    request.send(input.body);
  });
}

async function uploadToR2(input: {
  body: Blob;
  kind: MediaKind;
  contentType: string;
  onProgress?: (fraction: number) => void;
}): Promise<StoredMedia> {
  const createUpload = functions.httpsCallable("adminCreateMediaUpload", {
    timeout: 30000,
  });
  const createResult = await createUpload({
    kind: input.kind,
    contentType: input.contentType,
    sizeBytes: input.body.size,
  });
  const session = createResult.data as UploadSession;
  if (!session.uploadUrl) {
    throw new Error(`Cloudflare upload session is ${session.state}.`);
  }
  await putToSignedUrl({
    url: session.uploadUrl,
    headers: session.requiredHeaders || {
      "Content-Type": input.contentType,
    },
    body: input.body,
    onProgress: input.onProgress,
  });

  const finalize = functions.httpsCallable("adminFinalizeMediaUpload", {
    timeout: 540000,
  });
  const finalized = await finalize({ assetId: session.assetId });
  const asset = (finalized.data as { asset?: ReadyAsset }).asset;
  if (!asset?.deliveryUrl || asset.state !== "ready") {
    throw new Error(
      `Cloudflare media finalization is ${asset?.state || "unavailable"}.`
    );
  }
  return {
    url: asset.deliveryUrl,
    storageProvider: "r2",
    mediaAssetId: asset.id,
  };
}

async function uploadToFirebase(input: {
  body: Blob;
  storagePath: string;
  contentType: string;
  onProgress?: (fraction: number) => void;
}): Promise<StoredMedia> {
  const ref = storage.ref(input.storagePath);
  const task = ref.put(input.body, { contentType: input.contentType });
  await new Promise<void>((resolve, reject) => {
    task.on(
      firebase.storage.TaskEvent.STATE_CHANGED,
      (snapshot) => {
        if (input.onProgress && snapshot.totalBytes > 0) {
          input.onProgress(snapshot.bytesTransferred / snapshot.totalBytes);
        }
      },
      reject,
      resolve
    );
  });
  return {
    url: await task.snapshot.ref.getDownloadURL(),
    storageProvider: "firebase",
    storagePath: input.storagePath,
  };
}

/**
 * Provider compatibility boundary for NobleAdmin image/video writes.
 *
 * The returned `url` remains the authoritative field stored in Firestore/RTDB,
 * so ThePath and older builds do not need a schema migration. New uploads try
 * R2 first and can fall back to the existing Firebase path during rollout.
 */
export async function uploadMedia(input: {
  body: Blob;
  firebasePath: string;
  kind?: MediaKind;
  contentType?: string;
  fileName?: string;
  onProgress?: (fraction: number) => void;
}): Promise<StoredMedia> {
  const fileName =
    input.fileName ||
    (input.body instanceof File ? input.body.name : "") ||
    input.firebasePath;
  const contentType = normalizeMediaContentType(
    input.contentType || input.body.type,
    fileName
  );
  const kind = input.kind || inferMediaKind(contentType, fileName);
  if (!kind || !contentType) {
    throw new Error("Unable to determine the image/video content type.");
  }

  if (R2_MEDIA_ENABLED) {
    try {
      return await uploadToR2({
        body: input.body,
        kind,
        contentType,
        onProgress: input.onProgress,
      });
    } catch (error) {
      if (!FIREBASE_FALLBACK_ENABLED) throw error;
      // eslint-disable-next-line no-console
      console.warn("[media] R2 upload failed; using Firebase fallback.", error);
    }
  }
  return uploadToFirebase({
    body: input.body,
    storagePath: input.firebasePath,
    contentType,
    onProgress: input.onProgress,
  });
}

export async function deleteMedia(input: {
  url?: string | null;
  firebasePath?: string | null;
}): Promise<void> {
  const url = input.url?.trim() || "";
  const assetId = mediaAssetIdFromUrl(url);
  if (assetId) {
    const callable = functions.httpsCallable("adminDeleteMediaAsset", {
      timeout: 30000,
    });
    await callable({ assetId });
    return;
  }

  if (url && isFirebaseStorageUrl(url)) {
    await storage.refFromURL(url).delete();
    return;
  }
  if (input.firebasePath) {
    await storage.ref(input.firebasePath).delete();
  }
}
